import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarDays, Clock3 } from 'lucide-react';
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';
import { toast } from 'react-toastify';
import Layout from '../components/layout/Layout.tsx';
import DashboardHero from './DashboardHero.tsx';
import { ConversationCardsSection, isCardFromToday, type CardType } from "./ConversationCards.tsx";
import SideModal from './SideModal.tsx';
import api, { fetchBotStatus } from '../../../../services/api.js';
import { useAuthSession } from '../../../../contexts/AuthSessionContext.js';
import { useBranding } from '../../../../branding/BrandingContext.js';
import { fetchAgendaAppointments, fetchAgendaConfig } from '../services/api.ts';
import { DASHBOARD_POLL_INTERVAL_MS } from '../constants/timing.js';

// BG com bolinhas
const DotsBg = () => (
  <svg
    className="absolute inset-0 w-full h-full pointer-events-none"
    style={{ zIndex: 0 }}
    width="100%"
    height="100%"
    xmlns="http://www.w3.org/2000/svg"
  >
    <defs>
      <pattern id="smallGrid" width="26" height="26" patternUnits="userSpaceOnUse">
        <circle cx="1" cy="1" r="1.2" fill="#e4eaf5" />
      </pattern>
    </defs>
    <rect width="100%" height="100%" fill="url(#smallGrid)" />
  </svg>
);

const SOUND_NOTIFICATION = '/not.mp3';
const SOUND_PREFERENCE_KEY = 'atendabot.dashboard.muted';

const Dashboard = () => {
  const branding = useBranding();
  const { isFoodUser } = useAuthSession();
  const isFood =
    'isFood' in branding && typeof branding.isFood === 'boolean'
      ? branding.isFood
      : isFoodUser === true;
  const [modalConv, setModalConv] = useState<any>(null);
  const [human, setHuman] = useState<CardType[]>([]);
  const [bot, setBot] = useState<CardType[]>([]);
  const [firstLoad, setFirstLoad] = useState(true);
  const [todayAgenda, setTodayAgenda] = useState<{
    total: number;
    timezone: string;
    next: { startAt: string; clientName: string; serviceName: string } | null;
  } | null>(null);

  // Status global do bot
  const [botIsActive, setBotIsActive] = useState(true);
  const [isUpdatingBotStatus, setIsUpdatingBotStatus] = useState(false);
  const [showAttendantName, setShowAttendantName] = useState(false);
  const [isUpdatingShowAttendantName, setIsUpdatingShowAttendantName] = useState(false);
  const [isSoundMuted, setIsSoundMuted] = useState(() => {
    if (typeof window === 'undefined') return false;
    try {
      return window.localStorage.getItem(SOUND_PREFERENCE_KEY) === 'true';
    } catch {
      return false;
    }
  });

  // Refs para comparar mudanças
  const prevHumanRef = useRef<any[]>([]);
  const prevBotRef = useRef<any[]>([]);
  const isUpdatingShowAttendantNameRef = useRef(false);

  // Elementos de audio
  const audioSino = useRef<HTMLAudioElement>(null);
  const audioBeep = useRef<HTMLAudioElement>(null);
  const isSoundMutedRef = useRef(isSoundMuted);

  function playSound(type: 'humano' | 'bot') {
    if (isSoundMutedRef.current) return;
    if (type === 'humano' && audioSino.current) {
      audioSino.current.currentTime = 0;
      audioSino.current.play().catch(() => {});
    } else if (type === 'bot' && audioBeep.current) {
      audioBeep.current.currentTime = 0;
      audioBeep.current.play().catch(() => {});
    }
  }

  const handleToggleSound = () => {
    const nextMuted = !isSoundMutedRef.current;
    isSoundMutedRef.current = nextMuted;
    setIsSoundMuted(nextMuted);
    try {
      window.localStorage.setItem(SOUND_PREFERENCE_KEY, String(nextMuted));
    } catch {
      // O controle continua funcionando mesmo se o armazenamento estiver indisponível.
    }
    if (nextMuted) {
      audioSino.current?.pause();
      audioBeep.current?.pause();
    }
  };

  // Busca o status global do bot
  const fetchBotGlobalStatus = async () => {
    try {
      const status = await fetchBotStatus();
      setBotIsActive(status.isActive);
      if (!isUpdatingShowAttendantNameRef.current) {
        setShowAttendantName(status.nome_no_atendimento === true);
      }
    } catch (e) {
      setBotIsActive(true); // fallback: considera ativo se falhar
    }
  };

  const handleToggleBotStatus = async () => {
    if (isUpdatingBotStatus) return;

    const nextStatus = !botIsActive;
    setIsUpdatingBotStatus(true);
    setBotIsActive(nextStatus);

    try {
      await api.put('/atendabot/config', { atendabot_active: nextStatus });
      toast.success(nextStatus ? 'Atendabot ativado.' : 'Atendabot pausado.');
      await fetchBotGlobalStatus();
    } catch (e: any) {
      setBotIsActive(!nextStatus);
      if (e?.response?.data?.code === 'PROMPT_NOT_PUBLISHED') {
        toast.error(
          e?.response?.data?.error
          || 'Configure e publique o atendimento em "Configurações IA" antes de ativar o Atendabot.',
        );
      } else {
        toast.error('Não foi possível atualizar o status do Atendabot.');
      }
    } finally {
      setIsUpdatingBotStatus(false);
    }
  };

  const handleToggleShowAttendantName = async () => {
    if (isUpdatingShowAttendantName) return;

    const nextValue = !showAttendantName;
    isUpdatingShowAttendantNameRef.current = true;
    setIsUpdatingShowAttendantName(true);
    setShowAttendantName(nextValue);

    try {
      await api.put('/atendabot/config', { nome_no_atendimento: nextValue });
      toast.success(nextValue ? 'Nome do atendente será exibido.' : 'Nome do atendente oculto.');
    } catch (e) {
      setShowAttendantName(!nextValue);
      toast.error('Não foi possível atualizar a exibição do nome do atendente.');
    } finally {
      isUpdatingShowAttendantNameRef.current = false;
      setIsUpdatingShowAttendantName(false);
    }
  };

  // Busca todos os atendimentos via API + status do bot
  const fetchAll = async (showLoader = false) => {
    if (showLoader) setFirstLoad(true);
    try {
      const [h, b] = await Promise.all([
        api.get('/atendabot/atendimentos/humano').then(r => r.data),
        api.get('/atendabot/atendimentos/bot').then(r => r.data),
      ]);

      // Sons - só se última mensagem for do cliente
      h.forEach((card: any) => {
        const prev = prevHumanRef.current.find(c => c.id === card.id);
        if (
          prev &&
          prev.lastActivity !== card.lastActivity &&
          card.lastMsgFromMe // Somente cliente
        ) {
          playSound('humano');
        }
      });
      b.forEach((card: any) => {
        const prev = prevBotRef.current.find(c => c.id === card.id);
        if (
          prev &&
          prev.lastActivity !== card.lastActivity &&
          card.lastMsgFromMe // Somente cliente
        ) {
          playSound('bot');
        }
      });

      setHuman(h);
      setBot(b);
      prevHumanRef.current = h;
      prevBotRef.current = b;
    } catch (e) {
      toast.error('Erro ao buscar atendimentos.');
    }
    if (showLoader) setFirstLoad(false);
  };

  // Load inicial + pooling dos cards e do status do bot
  useEffect(() => {
    fetchAll(true);
    fetchBotGlobalStatus();
    const interval = setInterval(() => {
      fetchAll(false);
      fetchBotGlobalStatus();
    }, DASHBOARD_POLL_INTERVAL_MS);
    return () => clearInterval(interval);
    // Poll fixo no mount; incluir fetchAll/fetchBotGlobalStatus recriaria o intervalo a cada render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isFood) {
      setTodayAgenda(null);
      return undefined;
    }

    let disposed = false;
    const fetchTodayAgenda = async () => {
      if (document.visibilityState !== 'visible') return;
      try {
        const config = await fetchAgendaConfig();
        const dateKey = formatInTimeZone(new Date(), config.timezone, 'yyyy-MM-dd');
        const from = fromZonedTime(`${dateKey}T00:00:00`, config.timezone).toISOString();
        const to = fromZonedTime(`${dateKey}T23:59:59.999`, config.timezone).toISOString();
        const appointments = await fetchAgendaAppointments({
          from,
          to,
          status: 'scheduled,confirmed',
        });
        if (disposed) return;
        const now = Date.now();
        const next = appointments.find((appointment) => new Date(appointment.startAt).getTime() >= now);
        setTodayAgenda({
          total: appointments.length,
          timezone: config.timezone,
          next: next ? {
            startAt: next.startAt,
            clientName: next.client.name,
            serviceName: next.service.name,
          } : null,
        });
      } catch (_) {
        if (!disposed) setTodayAgenda(null);
      }
    };

    void fetchTodayAgenda();
    const interval = window.setInterval(fetchTodayAgenda, 30000);
    return () => {
      disposed = true;
      window.clearInterval(interval);
    };
  }, [isFood]);

  // Troca atendimento humano <-> bot
  const handleChangeType = async (card: any, type: string) => {
    // Se tentar mandar para bot e o bot estiver desligado, ignora
    if (type === "bot" && !botIsActive) {
      toast.warning('O Atendabot está desativado globalmente no momento.');
      return;
    }
    try {
      await api.put(`/atendabot/atendimentos/${card.id}/trocar`, {});
      fetchAll();
    } catch (e) {
      toast.error('Erro ao trocar atendimento.');
    }
  };

  // Finaliza atendimento
  const handleFinish = async (card: any, options?: { reactivateBot?: boolean }) => {
    try {
      await api.put(`/atendabot/atendimentos/${card.id}/finalizar`, {});
      if (options?.reactivateBot) {
        try {
          await api.put(`/atendabot/clientes/${card.id}/bot-status`, { active: true });
        } catch (reactivateError) {
          console.error('Erro ao reativar a IA após finalizar:', reactivateError);
          toast.warning('Conversa finalizada, mas houve um problema ao reativar a IA para este cliente.');
        }
      }
      fetchAll();
    } catch (e) {
      toast.error('Erro ao finalizar atendimento.');
    }
  };


  const todayHuman = human.filter(isCardFromToday);
  const todayBot = bot.filter(isCardFromToday);
  let heroHumanCount = todayHuman.length;
  let heroBotCount = todayBot.length;
  if (!botIsActive) {
    heroHumanCount = todayHuman.length + todayBot.length;
    heroBotCount = 0;
  }

  const humanCards = human.concat(!botIsActive ? bot : []);
  const botCards = bot;
  const shouldRenderBotFirst = humanCards.length === 0 && botCards.length > 0;

  const humanSection = (
    <ConversationCardsSection
      section="humano"
      cards={humanCards}
      onClickCard={setModalConv}
      onChangeType={handleChangeType}
      onFinish={handleFinish}
      botIsActive={botIsActive}
    />
  );

  const botSection = (
    <ConversationCardsSection
      section="bot"
      cards={botCards}
      onClickCard={setModalConv}
      onChangeType={handleChangeType}
      onFinish={handleFinish}
      botIsActive={botIsActive}
    />
  );

  return (
    <Layout>
      {/* Sons invisíveis */}
      <audio ref={audioSino} src={SOUND_NOTIFICATION} />
      <audio ref={audioBeep} src={SOUND_NOTIFICATION} />
      <div className="relative min-h-screen w-full px-3 py-10 overflow-x-hidden" style={{ background: "#f7fafc", fontFamily: 'Urbanist, sans-serif' }}>
        <DotsBg />
        <div className="relative z-10">
          <DashboardHero
            humanCount={heroHumanCount}
            botCount={heroBotCount}
            botIsActive={botIsActive}
            isUpdatingBotStatus={isUpdatingBotStatus}
            onToggleBotStatus={handleToggleBotStatus}
            showAttendantName={showAttendantName}
            isUpdatingShowAttendantName={isUpdatingShowAttendantName}
            onToggleShowAttendantName={handleToggleShowAttendantName}
            isSoundMuted={isSoundMuted}
            onToggleSound={handleToggleSound}
          />

          {!isFood && (
            <section className="mx-auto mb-8 w-full max-w-7xl px-1" aria-label="Agenda de hoje">
              <div className="flex flex-col gap-4 rounded-2xl border border-indigo-100 bg-white p-5 shadow-[0_18px_45px_rgba(50,47,132,0.08)] sm:flex-row sm:items-center sm:justify-between">
                <div className="flex min-w-0 items-center gap-4">
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[#322F84] text-white">
                    <CalendarDays size={24} />
                  </span>
                  <div className="min-w-0">
                    <p className="text-xs font-extrabold uppercase tracking-[0.12em] text-[#322F84]">Agenda de hoje</p>
                    <div className="mt-1 flex flex-wrap items-baseline gap-x-3 gap-y-1">
                      <strong className="text-2xl font-extrabold text-slate-900">{todayAgenda?.total ?? '—'}</strong>
                      <span className="text-sm text-slate-500">agendamento(s) ativo(s)</span>
                    </div>
                    <p className="mt-1 flex items-center gap-1.5 truncate text-sm text-slate-600">
                      <Clock3 size={14} className="shrink-0" />
                      {todayAgenda?.next
                        ? `Próxima às ${formatInTimeZone(todayAgenda.next.startAt, todayAgenda.timezone, 'HH:mm')} · ${todayAgenda.next.clientName} · ${todayAgenda.next.serviceName}`
                        : 'Nenhum próximo agendamento para hoje.'}
                    </p>
                  </div>
                </div>
                <Link to="/atendabot/agenda" className="inline-flex min-h-[42px] shrink-0 items-center justify-center rounded-xl bg-[#322F84] px-5 text-sm font-bold text-white shadow-lg shadow-indigo-950/15 transition hover:bg-[#282569]">
                  Abrir agenda
                </Link>
              </div>
            </section>
          )}

          {shouldRenderBotFirst ? (
            <>
              {botSection}
              {humanSection}
            </>
          ) : (
            <>
              {humanSection}
              {botSection}
            </>
          )}
        </div>
      </div>
      {modalConv && <SideModal key={modalConv.id} conv={modalConv} onClose={() => setModalConv(null)} />}

      {firstLoad && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/60 pointer-events-none">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-teal-500 border-t-transparent"></div>
        </div>
      )}
    </Layout>
  );
};

export default Dashboard;
